import observer from 'saddlebag-observer';

type LoginModalInitParams = {
  /**
   * Callback used to update the visibility of the login form
   * @param visible - indicating what the new visibility of the login form should be
   */
  setLoginFormVisible: (visible: boolean) => void;
};

/**
 * The header helper function required to support the ability to open the login form using the
 * `sid-login-button-clicked` observer event. This helper should be invoked at the root of your
 * application to ensure the subscriptions only happen once.
 *
 * NB: Ideally this wouldn't be required, but the observer events are heavily used across multiple microsites, so until
 * they can be identified and removed, it is mandatory to include this.
 *
 * @public
 *
 * @param setLoginFormVisible - A function to trigger opening the login form
 * @returns An empty promise
 *
 * @example
 * ```ts
 * import Header from '@skyscanner-internal/global-component/header';
 * import { loginModalInit } from '@skyscanner-internal/global-component/header/helpers';
 *
 *
 * const Layout = () => {
 *   const [loginFormVisible, setLoginFormVisible] = useState(false);
 *
 *   useEffect(() => {
 *     loginModalInit({setLoginFormVisible});
 *   }, [])
 *
 *   return <>
 *     <Header {...props} />
 *   </>
 * }
 * ```
 */
const loginModalInit = async ({ setLoginFormVisible }: LoginModalInitParams) => {
  observer.publish('sid-login-modal-loaded');

  observer.subscribe('sid-login-button-clicked', () => {
    setLoginFormVisible(true);
  });

  return Promise.resolve('Login form is initialised.');
};

export default loginModalInit;
